import { createMemo, createRoot, createSignal } from 'solid-js';
import { STARTER_DECK_STATS, STARTER_OWNED_TOOL_IDS, toolById } from '~/lib/tools';
import type { DeckStats, Tool } from '~/types/game';

const STAT_KEYS = ['processing', 'intrusion', 'stealth'] as const;

function zeroStats(): DeckStats {
  return { processing: 0, intrusion: 0, stealth: 0 };
}

/** The player's deck: three stats (the deployment budget), the owned toolset,
 *  and the equipped loadout. Module-level singleton like the other lib stores
 *  so it survives the `<Show>`-based tab remounts.
 *
 *  State is held in plain signals with a clear shape (stats + owned ids +
 *  equipped ids) so a step-6 persistence layer can serialize/hydrate it
 *  without restructuring. In-memory only for now; resets on reload. */
function createDeck() {
  const [stats] = createSignal<DeckStats>({ ...STARTER_DECK_STATS });
  const [ownedIds, setOwnedIds] = createSignal<string[]>([...STARTER_OWNED_TOOL_IDS]);
  const [equippedIds, setEquippedIds] = createSignal<string[]>([]);
  // RESERVED cosmetic surface — no mechanical effect. Cyberdeck skins are a
  // primary monetization surface (per the monetization research); the deck's
  // eventual visual rendering will read this. Nothing reads it yet. Mirrors the
  // reserve-don't-build pattern (non-POI targets, the `dual` role).
  const [skinId] = createSignal<string>('base');

  const resolve = (ids: string[]): Tool[] =>
    ids.map(toolById).filter((t): t is Tool => t !== undefined);

  const ownedTools = createMemo<Tool[]>(() => resolve(ownedIds()));
  const equippedTools = createMemo<Tool[]>(() => resolve(equippedIds()));

  /** Summed deployment cost of the equipped loadout, per stat. */
  const loadoutCost = createMemo<DeckStats>(() => {
    const c = zeroStats();
    for (const t of equippedTools()) {
      for (const k of STAT_KEYS) c[k] += t.cost[k];
    }
    return c;
  });

  /** True when any stat's loadout cost exceeds the deck budget. An over-budget
   *  loadout is flagged and not committable (the step-3 commit will gate on
   *  `isValid`). */
  const isOverBudget = createMemo<boolean>(() => {
    const s = stats();
    const c = loadoutCost();
    return STAT_KEYS.some((k) => c[k] > s[k]);
  });

  const isValid = createMemo<boolean>(() => !isOverBudget());

  const isEquipped = (id: string): boolean => equippedIds().includes(id);

  /** Equipping is unconstrained (the player can build an over-budget loadout
   *  and see it flagged); validity is what gates committing. */
  const equip = (id: string): void => {
    if (!toolById(id)) return;
    setEquippedIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };
  const unequip = (id: string): void => {
    setEquippedIds((ids) => ids.filter((x) => x !== id));
  };
  const toggle = (id: string): void => (isEquipped(id) ? unequip(id) : equip(id));

  const isOwned = (id: string): boolean => ownedIds().includes(id);

  /** Add a tool to the owned set (e.g. bought at a shop). Idempotent. The ¤
   *  debit is the caller's responsibility (player.spend). */
  const acquireTool = (id: string): void => {
    if (!toolById(id)) return;
    setOwnedIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };

  return {
    stats,
    ownedTools,
    equippedTools,
    equippedIds,
    loadoutCost,
    isOverBudget,
    isValid,
    isEquipped,
    isOwned,
    equip,
    unequip,
    toggle,
    acquireTool,
    /** Reserved cosmetic id; no mechanical effect (see above). */
    skinId,
  };
}

export const deck = createRoot(() => createDeck());
